import {
  CAMERA_PRESENTER,
  PASSED,
  VENDOR_AGORA,
  RESET_USER_VIRTUAL_BACKGROUND
} from '@studio/constants';
import {
  GenericCameraSourceSettings,
  AgoraCameraPreview
} from '@shoflo/polymer';
import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { ModalBody } from 'reactstrap';
import {
  clearModal,
  setAudioInputDevice,
  setAudioOutputDevice,
  setVideoInputDevice,
  setUserBackground,
  setTransformedVideoInputs,
  addToAttendeeTiles,
  showNotification,
  uploadUserVirtualBackground,
  deleteUserVirtualBackground
} from '@shoflo/studio-redux/actions';
import { GENERIC_AUDIO_VIDEO_SETTINGS_FORM } from '@studio/constants';
import { updatePresenterSettings } from 'actions/presenter';
import { StyledModal } from './SourceSettings.styled';
import setGenericAudioInputDevice from '@shoflo/studio-redux/actions/meeting/setGenericAudioInputDevice';
import {
  clearVirtualBackground as agoraClearVirtualBackground,
  getVideoInput as agoraGetVideoInput,
  initializeVirtualBackground as agoraInitializeVirtualBackground
} from '@cvent/studio-agora/virtualBackgrounds/virtualBackground';
import { switchVideoInput as agoraSwitchVideoInput } from '@cvent/studio-agora/video/videoHelpers';
import { switchAudioInput as agoraSwitchAudioInput } from '@cvent/studio-agora/audio/audioHelper';

const VENDOR_CONFIG_MAP = {
  [VENDOR_AGORA]: {
    cameraPreviewElement: AgoraCameraPreview,
    clearVirtualBackground: agoraClearVirtualBackground,
    initializeBackgroundFilter: agoraInitializeVirtualBackground,
    getVideoInput: agoraGetVideoInput,
    switchVideoInput: agoraSwitchVideoInput,
    switchAudioInput: agoraSwitchAudioInput
  }
};

const GenericSourceSettings = () => {
  const modal = useSelector(state => state.modal);
  const user = useSelector(state => state.user, shallowEqual);

  const client = useSelector(state => state.meeting.client, shallowEqual);
  const attendeeId = useSelector(state => state.meeting.attendeeId);
  const attendeeTiles = useSelector(state => state.meeting.attendeeTiles);

  const audioInputDevice = useSelector(state => state.audioInputDevice);
  const audioOutputDevice = useSelector(state => state.audioOutputDevice);
  const videoInputDevice = useSelector(state => state.videoInputDevice);
  const userBackground = useSelector(state => state.userBackground);
  const transformedVideoInputs = useSelector(
    state => state.transformedVideoInputs
  );
  const cameraStatus = useSelector(state => state.readinessCheck.camera);

  const vendor = modal?.initialValue?.vendor;
  const vendorConfig = VENDOR_CONFIG_MAP[vendor];

  const dispatch = useDispatch();

  const currentVideoTrack = attendeeTiles[attendeeId];

  const isCameraAvailable = cameraStatus === PASSED;
  const isPresenterEditForm = modal?.type === GENERIC_AUDIO_VIDEO_SETTINGS_FORM;
  if (!isPresenterEditForm) return null;

  const isOpen =
    isPresenterEditForm && modal.initialValue?.source.type === CAMERA_PRESENTER;

  const closeModal = () => dispatch(clearModal(modal.type));

  const onCancel = async initialDataPayload => {
    const { transformedVideoInputs, videoInput, videoTrack } =
      initialDataPayload;

    if (videoTrack) {
      dispatch(addToAttendeeTiles(attendeeId, videoTrack));
    }

    dispatch(setVideoInputDevice(videoInput));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    closeModal();
  };

  const onSubmit = data => {
    const {
      audioInputDeviceId,
      audioOutputDeviceId,
      videoInput,
      title,
      description,
      userBackground,
      transformedVideoInputs,
      videoTrack
    } = data;

    if (videoTrack) {
      dispatch(addToAttendeeTiles(attendeeId, videoTrack));
    }
    dispatch(setGenericAudioInputDevice(audioInputDeviceId));
    dispatch(setAudioInputDevice(audioInputDeviceId));
    dispatch(setVideoInputDevice(videoInput));
    dispatch(setAudioOutputDevice(audioOutputDeviceId));
    dispatch(setUserBackground(userBackground));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    dispatch(
      updatePresenterSettings(modal.initialValue.source, {
        source_title: title,
        description
      })
    );
    closeModal();
  };

  const { title, description = '' } = modal.initialValue.source;

  const handleDeleteUserVirtualBackground = () => {
    dispatch(deleteUserVirtualBackground());
  };
  const handleUploadUserVirtualBackground = file =>
    dispatch(uploadUserVirtualBackground(file));
  const handleResetUserVirtualBackground = () =>
    dispatch({ type: RESET_USER_VIRTUAL_BACKGROUND });

  const {
    cameraPreviewElement,
    clearVirtualBackground,
    initializeBackgroundFilter,
    getVideoInput,
    switchVideoInput,
    switchAudioInput
  } = vendorConfig;

  const deviceActions = {
    addToAttendeeTiles,
    handleVideoInputChanged: setVideoInputDevice,
    handleAudioInputChanged: setGenericAudioInputDevice,
    showNotification
  };

  return (
    <StyledModal isOpen={isOpen} backdrop="static" keyboard="false">
      <ModalBody className="p-1">
        <GenericCameraSourceSettings
          title={title}
          description={description}
          onSubmit={onSubmit}
          onCancel={onCancel}
          audioInputDeviceId={audioInputDevice}
          audioOutputDeviceId={audioOutputDevice}
          videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
          isCameraAvailable={isCameraAvailable}
          userBackground={userBackground}
          transformedVideoInputs={transformedVideoInputs}
          uploadUserVirtualBackground={handleUploadUserVirtualBackground}
          deleteUserVirtualBackground={handleDeleteUserVirtualBackground}
          resetUserVirtualBackground={handleResetUserVirtualBackground}
          user={user}
          CameraPreviewElement={cameraPreviewElement}
          clearVirtualBackground={clearVirtualBackground}
          initializeBackgroundFilter={initializeBackgroundFilter}
          getVideoInput={getVideoInput}
          switchVideoInput={switchVideoInput}
          switchAudioInput={switchAudioInput}
          currentVideoTrack={currentVideoTrack}
          client={client}
          dispatch={dispatch}
          deviceActions={deviceActions}
        />
      </ModalBody>
    </StyledModal>
  );
};

export { GenericSourceSettings };
